import React from "react";
import { Link } from "react-router-dom";

import yardVanImg from "../../assets/images/Home/chiller-van-for-refrigerated-transport.webp";
import highRoofVanImg from "../../assets/images/Home/high-roof-van-service-chiller-dubai-sharjah-rent-freeze.webp";

export const chillerVanRasAlKhorSections = [
  {
    type: "cvr-hero",
    title: "Chiller Van Rental in Ras Al Khor — Pre-Cooled Vans Dispatched From Our Industrial Area 1 Base",
    paragraphs: [
      <>Chiller Fleet Transport provides <strong>chiller van rental in Ras Al Khor</strong> from our own yard in Industrial Area 1, with 16 chiller vans stationed on site. Vans hold 0°C to 8°C across payloads of 1 to 1.5 tons, covering cold store cross-docking, wholesale distribution runs, and Al Aweer market movement across the district.</>,
      "A booking confirmed for a Ras Al Khor address puts a pre-cooled van at your dock in 30 to 45 minutes. The vehicle is not positioned across the city first — it pulls out of the yard already staged and at temperature.",
      "Rates start at AED 250 a day self-drive. Contact our team with your dock location and loading window to confirm availability.",
    ],
  },

  {
    type: "cvr-text-image",
    title: "Why a Ras Al Khor Booking Moves Faster Than Anywhere Else in Dubai",
    paragraphs: [
      "Our Ras Al Khor base is where the Dubai fleet lives. Vans are staged, calibrated and pre-cooled in the yard before any booking is taken, which removes the step that costs every other operator time.",
      "An operator dispatching from elsewhere in the city has to cross Dubai before the job begins. On a Ras Al Khor delivery that transit lands inside the district's busiest dock hours, and a van arriving at 08:45 for a dock that stops releasing at 08:30 has missed the slot regardless of how well the cargo was handled.",
      <>From our yard, the measured window from booking confirmation to a pre-cooled van at a loading dock in Industrial Areas 1, 2 or 3 is <strong>30 to 45 minutes</strong>. The cargo body is already at temperature when it arrives, so loading starts immediately rather than after a cool-down.</>,
      "That matters most on the two things Ras Al Khor runs on: cross-docking, where a perishable transfer has a narrow handover window, and same-day replacement, where a cooling fault on an active route is covered from the same yard rather than from across town.",
    ],
    image: yardVanImg,
    imageAlt: "Pre-cooled chiller van staged at the Ras Al Khor Industrial Area 1 yard",
    reverse: false,
    altBg: false,
  },

  {
    type: "cvr-text-only",
    title: "What Moves Through Ras Al Khor",
    paragraphs: [
      "The district is Dubai's warehouse and cold storage corridor, and four business types account for most of the chilled volume our vans carry here.",
      {
        list: [
          <><strong>3PL cold storage and logistics operators</strong> run cross-docking transfers straight out of high-bay commercial cold stores, where cargo moves from one temperature-controlled environment to another with minimal exposure in between.</>,
          <><strong>FMCG and food importers</strong> operate daily wholesale distribution runs supplying hotel chains, restaurant groups and supermarket networks across Dubai. These are multi-drop routes leaving Ras Al Khor in the morning.</>,
          <><strong>Meat and poultry processors</strong> move fresh carcass cuts, minced product and poultry from industrial processing units to retail outlets under continuous temperature control.</>,
          <><strong>Floral wholesalers</strong> dispatch imported cut flower consignments arriving through Dubai International Airport cargo, where the gap between airport cold store and florist is where most of the damage happens.</>,
        ],
      },
      { subheading: "Al Aweer Market Runs" },
      "The Central Fruit and Vegetable Market at Al Aweer sits directly across the E66 and E44 corridor from Ras Al Khor Industrial Area, and it trades on a pre-dawn clock.",
      <>Primary movement runs <strong>03:30 to 07:30</strong>. Bulk fresh produce, imported berries, leafy greens and exotic fruit arrive overnight from port and airport clearance and are redistributed to Dubai retail and hotel kitchens the same morning.</>,
      "Vans assigned to Al Aweer runs are pre-cooled overnight in the yard rather than brought down to temperature at 03:30. A load collected at that hour has no tolerance for a cargo body still cooling on the move.",
    ],
  },

  {
    type: "cvr-table",
    title: "Dock Congestion and Yard Staging",
    intro: [
      "Ras Al Khor's internal industrial streets are narrow, and they jam twice a day.",
      <><strong>Outward distribution peaks between 05:00 and 08:30</strong>, as cold stores release morning stock. <strong>Inward intake peaks between 16:00 and 19:30</strong>, when containers and factory deliveries arrive for overnight storage. A van arriving into either window without a confirmed slot joins a queue on a street with nowhere to wait.</>,
      "Dock infrastructure varies across the district. Larger 3PL facilities have elevated hydraulic dock levellers. Older trading warehouses have ground-level roller shutter ramps where cargo moves by hand across an apron. The two load at different speeds, and the difference decides how long a van sits with its doors open.",
      "Our answer is yard staging. Vans are held in the yard bay until the dock is confirmed clear, then released. The cargo body holds temperature stationary in the shade rather than idling in a queue on a hot street with the compressor fighting ambient heat.",
      <><strong>Release and intake windows across the district:</strong></>,
    ],
    columns: ["Movement", "Window", "What it covers"],
    rows: [
      ["Cargo release, outward", "04:00 – 11:30", "Fresh stock reaching retail and hospitality before morning prep"],
      ["Cargo intake, inward", "14:00 – 20:30", "Container and factory deliveries into overnight cold storage"],
    ],
  },

  {
    type: "cvr-table",
    title: "Chiller Van Rental Rates From Ras Al Khor",
    description: "Rates below cover runs originating at our Industrial Area 1 base. Transit times are door to door and assume no dock queue at either end.",
    columns: ["Destination", "Self-drive", "With driver", "Off-peak", "Peak"],
    rows: [
      ["Al Quoz", "AED 250", "AED 300", "15–20 min", "30–40 min"],
      ["Deira — Al Ras / Sabkha", "AED 250", "AED 300", "15–20 min", "35–50 min"],
      ["Al Barsha", "AED 250", "AED 300", "20–25 min", "35–45 min"],
      ["Business Bay / Downtown", "AED 300", "AED 350", "10–15 min", "25–35 min"],
      ["Dubai Marina / JBR", "AED 300", "AED 350", "25–35 min", "45–60 min"],
      ["Dubai Investment Park", "AED 350", "AED 400", "30–40 min", "50–60 min"],
      ["Jebel Ali — JAFZA", "AED 350", "AED 400", "35–45 min", "60+ min"],
    ],
    notes: [
      <>All figures are starting rates, confirmed against cargo type and duration before booking. Weekly and monthly arrangements carry lower per-day rates — full duration pricing is on our <Link to="/chiller-van-for-rent" className="internal-link">chiller van rental in Dubai page</Link>. UAE VAT applies where applicable.</>,
      "The peak transit column is worth reading against the dock windows above. A Jebel Ali run leaving at 07:30 arrives after 08:30, which is past the outward release window at the receiving end.",
    ],
    background: "#f8f9fa",
  },

  {
    type: "cvr-text-image",
    title: "Which Van Configuration Suits Ras Al Khor Work",
    paragraphs: [
      "Of the 16 chiller van for rent stationed at the base, both standard and high roof configurations are held, and the dock mix across the district is what decides which one goes out.",
      "High roof units carry the volume on cold store cross-docking and bulk wholesale runs where a single trip replaces two. Standard roof vans work the older warehouse ramps and the multi-drop city routes that follow a Ras Al Khor collection, where height clearance at the receiving end is less predictable than at the loading end.",
      <>Rear and side door access is available on both. Full model and payload detail is on our <Link to="/chiller-van-for-rent" className="internal-link">chiller van fleet specifications page</Link>.</>,
    ],
    image: highRoofVanImg,
    imageAlt: "High roof chiller van for cold store cross-docking and bulk wholesale runs in Ras Al Khor",
    reverse: true,
    altBg: true,
  },

  {
    type: "cvr-faq",
    title: "Frequently Asked Questions",
    faqs: [
      {
        q: "How quickly can a chiller van reach my facility in Ras Al Khor?",
        a: "Thirty to forty-five minutes from booking confirmation to a pre-cooled van at your dock, across Industrial Areas 1, 2 and 3. The van leaves our yard already at temperature, so loading begins on arrival rather than after a cool-down period.",
      },
      {
        q: "Do you cover Al Aweer market collections?",
        a: "Yes. Vans assigned to Al Aweer runs are pre-cooled overnight and dispatched into the 03:30 to 07:30 trading window. Bulk produce, berries and leafy greens moving to retail and hotel kitchens are the standard load on these runs.",
      },
      {
        q: "What does a chiller van for rent cost on a run out of Ras Al Khor?",
        a: "From AED 250 a day self-drive for shorter runs to Al Quoz, Deira or Al Barsha, rising to AED 350 for Jebel Ali and Dubai Investment Park. The table above lists each destination. Weekly and monthly hire reduces the per-day rate.",
      },
    ],
  },

  {
    type: "cta",
    title: "Book a Chiller Van in Ras Al Khor",
    paragraphs: [
      "Sixteen chiller vans are stationed at our Industrial Area 1 yard, pre-cooled and staged for same-hour dispatch across the district and citywide multi-drop routes.",
      "Tell us your dock location, your cargo and your loading window. If you are working on a release window or an Al Aweer collection, say so at booking — we stage the van against that time rather than dispatching on request.",
    ],
  },
];
